import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../utils/firebase';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { Student, isStudent } from '../../../utils/firebase';
import { toast } from 'react-toastify';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import 'react-toastify/dist/ReactToastify.css';

interface RoleAuction {
  id: string;
  roleId: string;
  roleName: string;
  roleDescription: string;
  currentBid: number;
  currentBidder: string | null;
  currentBidderName: string | null;
  minBid: number;
  startTime: Date;
  endTime: Date;
  status: 'pending' | 'active' | 'completed' | 'cancelled' | 'ready_to_assign';
  bidders: {
    studentId: string;
    studentName: string;
    bidAmount: number;
    bidTime: Date;
  }[];
  winner?: {
    studentId: string;
    studentName: string;
    bidAmount: number;
  };
  maxRoleHolders: number;
  participants: string[]; // 참여한 학생 ID 목록
  isAssigned: boolean; // 역할 할당 완료 여부
}

interface RoleAuctionSettings {
  enabled: boolean;
  auctionDuration: number;
  minBidIncrement: number;
  autoAssignOnEnd: boolean;
  defaultMaxRoleHolders: number;
  endCondition: 'time' | 'fixedTime' | 'manual';
  fixedEndTime?: Date;
  isAnonymous: boolean;
}

const RoleAuction: React.FC = () => {
  const { currentUser } = useAuth();
  const [activeAuctions, setActiveAuctions] = useState<RoleAuction[]>([]);
  const [myBids, setMyBids] = useState<{ [auctionId: string]: number }>({});
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState<RoleAuction | null>(null);
  const [bidAmount, setBidAmount] = useState<number>(0);
  const [myPoints, setMyPoints] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [auctionSettings, setAuctionSettings] = useState<RoleAuctionSettings>({
    enabled: false,
    auctionDuration: 30,
    minBidIncrement: 10,
    autoAssignOnEnd: true,
    defaultMaxRoleHolders: 1,
    endCondition: 'time',
    isAnonymous: false
  });
  const [isMobile, setIsMobile] = useState(false);

  // 활성 경매 모니터링
  useEffect(() => {
    if (!currentUser || !isStudent(currentUser)) return;

    const auctionsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/roleAuctions`);
    const unsubscribe = onSnapshot(auctionsRef, (snapshot) => {
      console.log('경매 데이터 로딩:', snapshot.docs.length, '개 문서');
      
      const auctionsData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('경매 문서 데이터:', data);
        return {
          ...data,
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
          bidders: data.bidders?.map((bidder: any) => ({
            ...bidder,
            bidTime: bidder.bidTime?.toDate()
          })) || [],
          maxRoleHolders: data.maxRoleHolders || 1,
          participants: data.participants || [],
          isAssigned: data.isAssigned || false
        } as RoleAuction;
      });
      
      const newActiveAuctions = auctionsData.filter(auction => auction.status === 'active');
      const completedAuctions = auctionsData.filter(auction => auction.status === 'completed');
      const cancelledAuctions = auctionsData.filter(auction => auction.status === 'cancelled');
      
      // 경매가 완료되거나 취소되었을 때 알림
      for (const auction of [...completedAuctions, ...cancelledAuctions]) {
        const myBid = auction.bidders.find(bidder => bidder.studentId === currentUser?.uid);
        if (myBid) {
          if (auction.status === 'completed') {
            if (auction.winner?.studentId === currentUser?.uid) {
              toast.success(`${auction.roleName} 경매에서 우승했습니다!`);
            } else {
              toast.info(`${auction.roleName} 경매가 완료되었습니다. 입찰 포인트가 환불되었습니다.`);
            }
          } else if (auction.status === 'cancelled') {
            toast.info(`${auction.roleName} 경매가 취소되었습니다. 입찰 포인트가 환불되었습니다.`);
          }
        }
      }
      
      console.log('활성 경매:', newActiveAuctions);
      setActiveAuctions(newActiveAuctions);
    }, (error) => {
      console.error('경매 데이터 로딩 에러:', error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // 내 포인트 실시간 모니터링
  useEffect(() => {
    if (!currentUser || !isStudent(currentUser)) return;

    const studentRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students`, currentUser.uid);
    const unsubscribe = onSnapshot(studentRef, (doc) => {
      if (doc.exists()) {
        const studentData = doc.data();
        setMyPoints(studentData.points || 0);
      }
    }, (error) => {
      console.error('Error listening to student points:', error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // 경매 설정 실시간 모니터링
  useEffect(() => {
    if (!currentUser) return;

    const settingsRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/roleAuctionSettings`, 'settings');
    const unsubscribe = onSnapshot(settingsRef, (doc) => {
      if (doc.exists()) {
        setAuctionSettings(doc.data() as RoleAuctionSettings);
      }
    }, (error) => {
      console.error('Error listening to auction settings:', error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // 모바일 감지 useEffect
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 입찰 다이얼로그 열기
  const handleOpenBidDialog = (auction: RoleAuction) => {
    setSelectedAuction(auction);
    // 최소 입찰 증가액을 고려한 자동 계산
    const minBidAmount = auction.currentBid + auctionSettings.minBidIncrement;
    setBidAmount(minBidAmount);
    setBidDialogOpen(true);
  };

  // 입찰 제출
  const handleSubmitBid = async () => {
    if (!selectedAuction || !currentUser || !isStudent(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      // 포인트 확인
      if (bidAmount > myPoints) {
        setError('보유 포인트가 부족합니다.');
        return;
      }

      // 최소 입찰가 확인 (최소 입찰 증가액 고려)
      const minRequiredBid = selectedAuction.currentBid + auctionSettings.minBidIncrement;
      if (bidAmount < minRequiredBid) {
        setError(`최소 ${minRequiredBid.toLocaleString()} 포인트 이상 입력해주세요. (현재 최고 입찰가 + 최소 증가액)`);
        return;
      }

      const functions = getFunctions(getApp());
      const placeBid = httpsCallable<
        { schoolId: string; classId: string; auctionId: string; bidAmount: number },
        { points: number; selected: boolean }
      >(functions, 'placeRoleAuctionBid');
      const response = await placeBid({
        schoolId,
        classId,
        auctionId: selectedAuction.id,
        bidAmount,
      });

      setMyPoints(response.data.points);
      setBidDialogOpen(false);
      setSelectedAuction(null);
      setBidAmount(0);

      if (response.data.selected) {
        toast.success('입찰이 성공적으로 제출되었습니다!');
      } else {
        toast.info('입찰이 제출되었지만 선별되지 않았습니다. 포인트가 환불됩니다.');
      }
    } catch (err) {
      console.error('Error submitting bid:', err);
      const message = err instanceof Error ? err.message : '';
      setError(message && message !== 'internal' ? message : '입찰 제출 중 오류가 발생했습니다.');
    }
  };

  // 경매 남은 시간 계산
  const getTimeRemaining = (endTime: Date) => {
    const now = new Date();
    const diff = endTime.getTime() - now.getTime();
    
    if (diff <= 0) return '종료됨';
    
    const minutes = Math.floor(diff / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${minutes}분 ${seconds}초`;
  };



  // 내 입찰 기록 가져오기
  const getMyBid = (auction: RoleAuction) => {
    const myBid = auction.bidders.find(bidder => bidder.studentId === currentUser?.uid);
    return myBid?.bidAmount || 0;
  };

  // 내가 현재 입찰 중인지 확인 (동시 역할자 수 고려)
  const isCurrentlyBidding = () => {
    return activeAuctions.some(auction => {
      const myBid = auction.bidders.find(bidder => bidder.studentId === currentUser?.uid);
      if (!myBid) return false;
      
      // 입찰자들을 금액 순으로 정렬
      const sortedBidders = [...auction.bidders].sort((a, b) => b.bidAmount - a.bidAmount);
      const myRank = sortedBidders.findIndex(bidder => bidder.studentId === currentUser?.uid);
      
      // 동시 역할자 수 내에 있는지 확인
      return myRank < auction.maxRoleHolders;
    });
  };

  // 내가 입찰 중인 경매 찾기
  const getMyBiddingAuction = () => {
    return activeAuctions.find(auction => {
      const myBid = auction.bidders.find(bidder => bidder.studentId === currentUser?.uid);
      if (!myBid) return false;
      
      const sortedBidders = [...auction.bidders].sort((a, b) => b.bidAmount - a.bidAmount);
      const myRank = sortedBidders.findIndex(bidder => bidder.studentId === currentUser?.uid);
      
      return myRank < auction.maxRoleHolders;
    });
  };

  // 특정 경매에서 내가 선별된 입찰자인지 확인
  const isSelectedBidder = (auction: RoleAuction) => {
    const myBid = auction.bidders.find(bidder => bidder.studentId === currentUser?.uid);
    if (!myBid) return false;
    
    const sortedBidders = [...auction.bidders].sort((a, b) => b.bidAmount - a.bidAmount);
    const myRank = sortedBidders.findIndex(bidder => bidder.studentId === currentUser?.uid);
    
    return myRank < auction.maxRoleHolders;
  };

  if (!currentUser || !isStudent(currentUser)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ padding: '12px', background: '#fee', color: '#c33', borderRadius: '4px', border: '1px solid #fcc' }}>
          학생 정보를 불러올 수 없습니다.
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      height: '100%',
      minHeight: 0,
      position: 'relative', 
      background: 'transparent', 
      overflow: 'hidden',
      overflowX: 'hidden',
      paddingLeft: '0',
      paddingRight: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '32px'
    }}>
      {error && (
        <div style={{ 
          marginBottom: '16px', 
          position: 'relative', 
          top: 'auto', 
          left: 0, 
          right: 0, 
          zIndex: 1000, 
          padding: '12px', 
          background: '#fee', 
          color: '#c33', 
          borderRadius: '4px', 
          border: '1px solid #fcc',
          marginTop: '0'
        }}>
          {error}
          <button 
            onClick={() => setError(null)}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              color: '#c33',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              padding: '0 8px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div style={{ 
          marginBottom: '16px', 
          position: 'relative', 
          top: 'auto', 
          left: 0, 
          right: 0, 
          zIndex: 1000, 
          padding: '12px', 
          background: '#d4edda', 
          color: '#155724', 
          borderRadius: '4px', 
          border: '1px solid #c3e6cb',
          marginTop: '0'
        }}>
          {success}
          <button 
            onClick={() => setSuccess(null)}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              color: '#155724',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              padding: '0 8px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* 페이지 헤더 - 스크롤 영역 밖 */}
      <div style={{
        width: '1280px',
        maxWidth: '100%',
        margin: '0 auto',
        flexShrink: 0,
        paddingTop: '0',
        paddingBottom: '0',
        paddingLeft: isMobile ? '20px' : '0',
        paddingRight: isMobile ? '20px' : '0'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: isMobile ? 'space-between' : 'flex-start',
          gap: '16px'
        }}>
          <h1 style={{ 
            color: '#161616', 
            fontSize: isMobile ? '24px' : '32px', 
            fontFamily: 'pretendard', 
            fontWeight: '600', 
            lineHeight: isMobile ? '36px' : '48px',
            margin: 0
          }}>
            역할 경매
          </h1>
          {isMobile && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px' 
            }}>
              <span style={{ 
                color: '#161616', 
                fontSize: '14px', 
                fontFamily: 'pretendard', 
                fontWeight: '600', 
                lineHeight: '21px' 
              }}>
                나의 보유 포인트
              </span>
              <span style={{ 
                color: '#FF6F0F', 
                fontSize: '16px', 
                fontFamily: 'pretendard', 
                fontWeight: '600', 
                lineHeight: '24px' 
              }}>
                {myPoints.toLocaleString()} P
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div style={{
        width: '1280px',
        maxWidth: '100%',
        margin: '0 auto',
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingLeft: isMobile ? '20px' : '0',
        paddingRight: isMobile ? '20px' : '0',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}>
        {/* 콘텐츠 영역 */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '28px',
          flex: '0 0 auto'
        }}>
          {/* 포인트 및 경매 정보 */}
          <div style={{ 
            paddingLeft: '28px', 
            paddingRight: '28px', 
            paddingTop: '16px', 
            paddingBottom: '16px', 
            background: 'white', 
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {!isMobile && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px' 
              }}>
                <span style={{ 
                  color: '#161616', 
                  fontSize: '18px', 
                  fontFamily: 'pretendard', 
                  fontWeight: '600', 
                  lineHeight: '27px' 
                }}>
                  나의 보유 포인트
                </span>
                <span style={{ 
                  color: '#FF6F0F', 
                  fontSize: '18px', 
                  fontFamily: 'pretendard', 
                  fontWeight: '600', 
                  lineHeight: '27px' 
                }}>
                  {myPoints.toLocaleString()} P
                </span>
              </div>
            )}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              gap: '20px',
              flexWrap: isMobile ? 'wrap' : 'nowrap'
            }}>
              {activeAuctions.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px' 
                }}>
                  <span style={{ 
                    color: '#4C4C4C', 
                    fontSize: '14px', 
                    fontFamily: 'pretendard', 
                    fontWeight: '500', 
                    lineHeight: '21px' 
                  }}>
                    남은 시간:
                  </span>
                  <span style={{ 
                    color: '#161616', 
                    fontSize: '14px', 
                    fontFamily: 'pretendard', 
                    fontWeight: '600', 
                    lineHeight: '21px' 
                  }}>
                    {getTimeRemaining(activeAuctions[0].endTime)}
                  </span>
                </div>
              )}
              {isCurrentlyBidding() && (
                <div style={{ 
                  paddingLeft: '12px', 
                  paddingRight: '12px', 
                  paddingTop: '6px', 
                  paddingBottom: '6px', 
                  background: '#FFF3E0', 
                  borderRadius: '8px',
                  border: '1px solid #FFB74D'
                }}>
                  <span style={{ 
                    color: '#E65100', 
                    fontSize: '12px', 
                    fontFamily: 'pretendard', 
                    fontWeight: '600', 
                    lineHeight: '18px' 
                  }}>
                    {getMyBiddingAuction()?.roleName} 입찰 중
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 활성 경매 목록 */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '16px'
          }}>
            {activeAuctions.length === 0 ? (
              <div style={{ 
                paddingLeft: '28px', 
                paddingRight: '28px', 
                paddingTop: '24px', 
                paddingBottom: '24px', 
                background: 'white', 
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <span style={{ 
                  color: '#989898', 
                  fontSize: '16px', 
                  fontFamily: 'pretendard', 
                  fontWeight: '500', 
                  lineHeight: '24px' 
                }}>
                  현재 진행 중인 경매가 없습니다.
                </span>
              </div>
            ) : (
              activeAuctions.map((auction) => {
                const myBid = getMyBid(auction);
                const isMyBidHighest = auction.currentBidder === currentUser?.uid;
                const isCurrentlyBiddingOnThis = myBid > 0;
                const isSelectedInThis = isSelectedBidder(auction);
                const canBidOnThis = !isCurrentlyBidding() || isSelectedInThis;
                const alreadyBiddingOnThis = isCurrentlyBiddingOnThis;

                return (
                  <div 
                    key={auction.id} 
                    style={{ 
                      paddingLeft: '28px', 
                      paddingRight: '28px', 
                      paddingTop: '24px', 
                      paddingBottom: '24px', 
                      background: 'white', 
                      borderRadius: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px'
                    }}
                  >
                    {/* 경매 헤더 */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start', 
                      gap: '20px',
                      flexWrap: isMobile ? 'wrap' : 'nowrap'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h2 style={{ 
                          color: '#FF6F0F', 
                          fontSize: '20px', 
                          fontFamily: 'pretendard', 
                          fontWeight: '600', 
                          lineHeight: '30px',
                          margin: '0 0 8px 0'
                        }}>
                          {auction.roleName}
                        </h2>
                        <p style={{ 
                          color: '#4C4C4C', 
                          fontSize: '14px', 
                          fontFamily: 'pretendard', 
                          fontWeight: '400', 
                          lineHeight: '21px',
                          margin: 0
                        }}>
                          {auction.roleDescription}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          color: '#FF3D00', 
                          fontSize: '24px', 
                          fontFamily: 'pretendard', 
                          fontWeight: '700', 
                          lineHeight: '36px',
                          marginBottom: '4px'
                        }}>
                          {auction.currentBid.toLocaleString()} P
                        </div>
                        <div style={{ 
                          color: '#989898', 
                          fontSize: '12px', 
                          fontFamily: 'pretendard', 
                          fontWeight: '500', 
                          lineHeight: '18px' 
                        }}>
                          현재 최고 입찰
                        </div>
                      </div>
                    </div>

                    {/* 입찰 정보 */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      gap: '20px',
                      flexWrap: isMobile ? 'wrap' : 'nowrap'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ 
                          color: '#161616', 
                          fontSize: '14px', 
                          fontFamily: 'pretendard', 
                          fontWeight: '500', 
                          lineHeight: '21px' 
                        }}>
                          최소 입찰가: {auction.minBid.toLocaleString()} 포인트
                        </div>
                        <div style={{ 
                          color: '#161616', 
                          fontSize: '14px', 
                          fontFamily: 'pretendard', 
                          fontWeight: '500', 
                          lineHeight: '21px' 
                        }}>
                          역할 가능 인원: {auction.bidders.length}/{auction.maxRoleHolders}명
                        </div>
                        {auctionSettings.isAnonymous && (
                          <div style={{ 
                            color: '#FF6F0F', 
                            fontSize: '12px', 
                            fontFamily: 'pretendard', 
                            fontWeight: '500', 
                            lineHeight: '18px',
                            fontStyle: 'italic'
                          }}>
                            🔒 익명 경매
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {myBid > 0 && (
                          <div style={{ 
                            paddingLeft: '12px', 
                            paddingRight: '12px', 
                            paddingTop: '6px', 
                            paddingBottom: '6px', 
                            background: isSelectedInThis ? '#E8F5E9' : '#FFEBEE', 
                            borderRadius: '8px',
                            border: `1px solid ${isSelectedInThis ? '#4CAF50' : '#EF5350'}`
                          }}>
                            <span style={{ 
                              color: isSelectedInThis ? '#2E7D32' : '#C62828', 
                              fontSize: '12px', 
                              fontFamily: 'pretendard', 
                              fontWeight: '600', 
                              lineHeight: '18px' 
                            }}>
                              내 입찰: {myBid.toLocaleString()} 포인트
                            </span>
                          </div>
                        )}
                        {isCurrentlyBiddingOnThis && !isSelectedInThis && (
                          <div style={{ 
                            paddingLeft: '12px', 
                            paddingRight: '12px', 
                            paddingTop: '6px', 
                            paddingBottom: '6px', 
                            background: '#FFEBEE', 
                            borderRadius: '8px',
                            border: '1px solid #EF5350'
                          }}>
                            <span style={{ 
                              color: '#C62828', 
                              fontSize: '12px', 
                              fontFamily: 'pretendard', 
                              fontWeight: '600', 
                              lineHeight: '18px' 
                            }}>
                              선별되지 않음
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 입찰자 목록 */}
                    {auction.bidders.length > 0 && (
                      <div>
                        <div style={{ 
                          color: '#161616', 
                          fontSize: '14px', 
                          fontFamily: 'pretendard', 
                          fontWeight: '600', 
                          lineHeight: '21px',
                          marginBottom: '12px'
                        }}>
                          현재 입찰자 ({auction.bidders.length}명)
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '8px' 
                        }}>
                          {auction.bidders
                            .sort((a, b) => b.bidAmount - a.bidAmount)
                            .map((bidder, index) => (
                              <div 
                                key={bidder.studentId} 
                                style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '8px 12px',
                                  background: bidder.studentId === currentUser?.uid ? '#F5F5F5' : 'transparent',
                                  borderRadius: '8px'
                                }}
                              >
                                <div>
                                  <div style={{ 
                                    color: '#161616', 
                                    fontSize: '14px', 
                                    fontFamily: 'pretendard', 
                                    fontWeight: '500', 
                                    lineHeight: '21px' 
                                  }}>
                                    {auctionSettings.isAnonymous 
                                      ? `익명 입찰자 ${index + 1} (${bidder.bidAmount.toLocaleString()} 포인트)`
                                      : `${bidder.studentName} (${bidder.bidAmount.toLocaleString()} 포인트)`
                                    }
                                  </div>
                                  <div style={{ 
                                    color: '#989898', 
                                    fontSize: '12px', 
                                    fontFamily: 'pretendard', 
                                    fontWeight: '400', 
                                    lineHeight: '18px' 
                                  }}>
                                    {index + 1}위
                                  </div>
                                </div>
                                {bidder.studentId === currentUser?.uid && (
                                  <div style={{ 
                                    paddingLeft: '8px', 
                                    paddingRight: '8px', 
                                    paddingTop: '4px', 
                                    paddingBottom: '4px', 
                                    background: '#FF6F0F', 
                                    borderRadius: '6px'
                                  }}>
                                    <span style={{ 
                                      color: 'white', 
                                      fontSize: '10px', 
                                      fontFamily: 'pretendard', 
                                      fontWeight: '600', 
                                      lineHeight: '15px' 
                                    }}>
                                      내 입찰
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* 입찰 버튼 */}
                    <button
                      onClick={() => handleOpenBidDialog(auction)}
                      disabled={!canBidOnThis || alreadyBiddingOnThis}
                      style={{
                        width: '100%',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        background: (!canBidOnThis || alreadyBiddingOnThis) ? '#E9E9E9' : '#FF6F0F',
                        borderRadius: '8px',
                        border: 'none',
                        color: (!canBidOnThis || alreadyBiddingOnThis) ? '#989898' : 'white',
                        fontSize: '14px',
                        fontFamily: 'pretendard',
                        fontWeight: '600',
                        lineHeight: '21px',
                        cursor: (!canBidOnThis || alreadyBiddingOnThis) ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => {
                        if (canBidOnThis && !alreadyBiddingOnThis) {
                          e.currentTarget.style.background = '#E55A00';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (canBidOnThis && !alreadyBiddingOnThis) {
                          e.currentTarget.style.background = '#FF6F0F';
                        }
                      }}
                    >
                      {!canBidOnThis 
                        ? (isCurrentlyBiddingOnThis && !isSelectedInThis ? '선별되지 않음' : '다른 경매 입찰 중') 
                        : alreadyBiddingOnThis 
                          ? '이미 입찰 중'
                          : '입찰하기'
                      }
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* 입찰 다이얼로그 */}
      {bidDialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }} onClick={() => setBidDialogOpen(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ 
              marginTop: 0, 
              marginBottom: '16px',
              color: '#161616',
              fontSize: '20px',
              fontFamily: 'pretendard',
              fontWeight: '600',
              lineHeight: '30px'
            }}>
              입찰하기
            </h2>
            {selectedAuction && (
              <div>
                <h3 style={{ 
                  marginTop: 0, 
                  marginBottom: '8px',
                  color: '#161616',
                  fontSize: '18px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '27px'
                }}>
                  {selectedAuction.roleName}
                </h3>
                <p style={{ 
                  marginTop: 0, 
                  marginBottom: '16px',
                  color: '#4C4C4C',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '400',
                  lineHeight: '21px'
                }}>
                  {selectedAuction.roleDescription}
                </p>
                
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <div style={{ 
                    color: '#161616',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '21px',
                    marginBottom: '8px'
                  }}>
                    현재 최고 입찰: {selectedAuction.currentBid.toLocaleString()} 포인트
                  </div>
                  <div style={{ 
                    color: myPoints < bidAmount ? '#FF3D00' : '#161616',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '21px',
                    marginBottom: '8px'
                  }}>
                    내 보유 포인트: {myPoints.toLocaleString()} 포인트
                  </div>
                  {myPoints < bidAmount && (
                    <div style={{ 
                      color: '#FF3D00',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '21px',
                      marginTop: '8px'
                    }}>
                      ⚠️ 보유 포인트가 부족합니다!
                    </div>
                  )}
                </div>

                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block',
                    color: '#161616',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: '21px',
                    marginBottom: '8px'
                  }}>
                    입찰 금액
                  </label>
                  <input
                    type="number"
                    value={bidAmount || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setBidAmount(0);
                        return;
                      }
                      if (value.includes('.')) {
                        return;
                      }
                      const numValue = Number(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setBidAmount(numValue);
                      }
                    }}
                    min={selectedAuction.currentBid + auctionSettings.minBidIncrement}
                    max={myPoints}
                    step={1}
                    style={{
                      width: '100%',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      border: '1px solid #F0F0F0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '400',
                      lineHeight: '21px',
                      color: '#161616',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ 
                    color: '#989898',
                    fontSize: '12px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '18px',
                    marginTop: '8px'
                  }}>
                    최소 {(selectedAuction.currentBid + auctionSettings.minBidIncrement).toLocaleString()} 포인트 이상 입력해주세요. (현재 최고 입찰가 + 최소 증가액)
                  </div>
                </div>

                {bidAmount > myPoints && (
                  <div style={{ 
                    padding: '12px',
                    background: '#FFEBEE',
                    borderRadius: '8px',
                    border: '1px solid #EF5350',
                    marginTop: '16px'
                  }}>
                    <div style={{ 
                      color: '#C62828',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '21px'
                    }}>
                      보유 포인트가 부족합니다.
                    </div>
                  </div>
                )}
              </div>
            )}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '8px', 
              marginTop: '24px' 
            }}>
              <button 
                onClick={() => setBidDialogOpen(false)}
                style={{ 
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  border: '1px solid #F0F0F0',
                  background: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#161616',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '21px'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#F5F5F5'}
                onMouseOut={(e) => e.currentTarget.style.background = 'white'}
              >
                취소
              </button>
              <button 
                onClick={handleSubmitBid}
                disabled={
                  !selectedAuction || 
                  bidAmount < (selectedAuction.currentBid + auctionSettings.minBidIncrement) || 
                  bidAmount > myPoints
                }
                style={{ 
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  border: 'none',
                  background: (!selectedAuction || 
                    bidAmount < (selectedAuction.currentBid + auctionSettings.minBidIncrement) || 
                    bidAmount > myPoints) ? '#E9E9E9' : '#FF6F0F',
                  borderRadius: '8px',
                  cursor: (!selectedAuction || 
                    bidAmount < (selectedAuction.currentBid + auctionSettings.minBidIncrement) || 
                    bidAmount > myPoints) ? 'not-allowed' : 'pointer',
                  color: (!selectedAuction || 
                    bidAmount < (selectedAuction.currentBid + auctionSettings.minBidIncrement) || 
                    bidAmount > myPoints) ? '#989898' : 'white',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '21px'
                }}
                onMouseOver={(e) => {
                  if (selectedAuction && 
                    bidAmount >= (selectedAuction.currentBid + auctionSettings.minBidIncrement) && 
                    bidAmount <= myPoints) {
                    e.currentTarget.style.background = '#E55A00';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedAuction && 
                    bidAmount >= (selectedAuction.currentBid + auctionSettings.minBidIncrement) && 
                    bidAmount <= myPoints) {
                    e.currentTarget.style.background = '#FF6F0F';
                  }
                }}
              >
                입찰 제출
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleAuction;
